import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { HelpProvider } from './contexts/HelpContext';
import { ProtectedRoute, Login, Register, ModernRegister, EmailVerification, ForgotPassword, ResetPassword } from './features/auth';
import Layout from './components/common/Layout';
import Dashboard from './components/dashboard/Dashboard';
import HomePage from './components/dashboard/HomePage';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import SuperAdminOverview from './components/dashboard/SuperAdminOverview';
import Customers from './pages/Customers';
import Users from './pages/Users';
import Items from './pages/Items';
import Debts from './pages/Debts';
import Expenses from './pages/Expenses';
import { SalesPage } from './pages/SalesPage';
import Profile from './pages/Profile';
import Help from './pages/Help';
import FAQ from './pages/FAQ';
import Landing from './pages/Landing';
import Brand from './pages/Brand';
import Scan from './pages/Scan';
import { CustomerDetail } from './features/customers';
import { Role } from './types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Unauthorized from './components/auth/Unauthorized';
import { APP_CONFIG } from './utils/constants';

const routeTitleKeys: Record<string, string> = {
  brand: 'brand.pageTitle',
  scan: 'barcode.testTitle',
  dashboard: 'nav.dashboard',
  reports: 'nav.reports',
  companies: 'nav.companies',
  users: 'nav.users',
  customers: 'nav.customers',
  items: 'nav.items',
  sales: 'nav.sales',
  debts: 'nav.debts',
  expenses: 'nav.expenses',
  profile: 'nav.profile',
  help: 'nav.help',
  faq: 'nav.faq',
  login: 'auth.login',
  register: 'auth.createAccount',
  'register-old': 'auth.register',
  'verify-email': 'auth.emailVerification',
  'forgot-password': 'auth.forgotPassword',
  'reset-password': 'auth.resetPassword',
  unauthorized: 'auth.accessDeniedTitle',
};

const RouteDocumentTitle: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const route = pathname.split('/').filter(Boolean)[0] || '';
  const title = route
    ? routeTitleKeys[route] ? t(routeTitleKeys[route]) : t('app.description')
    : APP_CONFIG.NAME;

  return <Helmet><title>{title}</title></Helmet>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const OptionalAuthenticatedLayout: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return user ? <Layout /> : <Outlet />;
};

const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  return user?.role === Role.SUPER_ADMIN ? <SuperAdminOverview /> : <HomePage />;
};

function App() {
  return (
    <HelmetProvider>
      <div data-testid="app-root">
        <LanguageProvider>
          <ThemeProvider>
            <HelpProvider>
              <AuthProvider>
                <QueryClientProvider client={queryClient}>
                  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <RouteDocumentTitle />
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Landing />} />
                    <Route path="/scan" element={<Scan />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<ModernRegister />} />
                    <Route path="/register-old" element={<Register />} />
                    <Route path="/verify-email" element={<EmailVerification />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Public information pages adopt the app shell for signed-in users. */}
                    <Route element={<OptionalAuthenticatedLayout />}>
                      <Route path="/brand" element={<Brand />} />
                      <Route path="/help" element={<Help />} />
                      <Route path="/help/:guideSlug" element={<Help />} />
                      <Route path="/faq" element={<FAQ />} />
                    </Route>
                    
                    {/* Protected routes */}
                    <Route
                      element={
                        <ProtectedRoute>
                          <Layout />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="dashboard" element={<DashboardHome />} />
                      <Route path="reports" element={<Dashboard />} />
                      
                      {/* Super Admin only routes */}
                      <Route
                        path="companies"
                        element={
                          <ProtectedRoute allowedRoles={[Role.SUPER_ADMIN]}>
                            <Companies />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="companies/:companyId"
                        element={
                          <ProtectedRoute allowedRoles={[Role.SUPER_ADMIN]}>
                            <CompanyDetail />
                          </ProtectedRoute>
                        }
                      />
                      
                      {/* Admin+ routes */}
                      <Route
                        path="users"
                        element={
                          <ProtectedRoute allowedRoles={[Role.SUPER_ADMIN, Role.COMPANY_ADMIN]}>
                            <Users />
                          </ProtectedRoute>
                        }
                      />
                      
                      {/* All authenticated users */}
                      <Route path="customers" element={<Customers />} />
                      <Route path="customers/:customerId" element={<CustomerDetail />} />
                      <Route path="items" element={<Items />} />
                      
                      {/* Admin+ and Employee routes */}
                      <Route
                        path="sales"
                        element={
                          <ProtectedRoute allowedRoles={[Role.SUPER_ADMIN, Role.COMPANY_ADMIN, Role.EMPLOYEE]}>
                            <SalesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="debts"
                        element={
                          <ProtectedRoute allowedRoles={[Role.SUPER_ADMIN, Role.COMPANY_ADMIN, Role.EMPLOYEE]}>
                            <Debts />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="expenses"
                        element={
                          <ProtectedRoute allowedRoles={[Role.SUPER_ADMIN, Role.COMPANY_ADMIN]}>
                            <Expenses />
                          </ProtectedRoute>
                        }
                      />
                      
                      <Route path="profile" element={<Profile />} />
                    </Route>
                    
                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </Router>
                </QueryClientProvider>
              </AuthProvider>
            </HelpProvider>
          </ThemeProvider>
        </LanguageProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
